/**
 * Multi events menu: selectable list of events/custom events to go inside
 * mp-drop-menu
 */
import partition from 'lodash/partition';

import {pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {EventsMenu} from '../events-menu';
import template from './index.jade';

export class MultiEventsMenu extends EventsMenu {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      inverted: `boolean`,
    };
  }

  get config() {
    return {
      ...super.config,
      template,

      defaultState: {
        ...super.config.defaultState,
        sectionItems: [],
        inverted: false,
        selectedLabel: `Hide Events`,
        stagedSelected: new Set(),
      },

      helpers: {
        ...super.config.helpers,
        getCommitLabel: () => this.getCommitLabel(),
        handleSelectAll: (ev) => this.handleSelectAll(ev),
        handleSelectAllMatching: (ev) => this.handleSelectAllMatching(ev),
        onCommitClick: () => this.onCommitClick(),
        canCommit: () => this.state.stagedSelected.size !== this.state.eventData.length,
      },
    };
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);

    if (attr === `open` && this.attr(`open`) === false) {
      this.resetStagedSelection();
      this.updateSections(this.state.eventData, true);
    } else if (attr === `inverted` || attr === `selected`) {
      this.handleSelectionChange();
    }
  }

  transformSelectedVal(val) {
    return new Set(val || []);
  }

  updateEventItems() {
    super.updateEventItems();
    this.handleSelectionChange();
  }

  handleSelectAll(ev) {
    const {selectionState} = ev.detail;
    const {eventData, sectionItems} = this.state;
    const selectedItems = selectionState === `all` ? [] : eventData.map((event) => event.value);

    this.update({stagedSelected: new Set(selectedItems)});
    this.updateSections(sectionItems);
  }

  handleSelectAllMatching(ev) {
    const {sectionItems, stagedSelected} = this.state;
    const matchingSections = ev.detail.matchingSections;

    const allMatchingEventsSelected = !matchingSections.some((section) =>
      section.items.some((item) => !stagedSelected.has(item.value)),
    );

    const updatedStagedSelected = new Set(stagedSelected);
    matchingSections.forEach((section) =>
      section.items.forEach((item) =>
        allMatchingEventsSelected ? updatedStagedSelected.delete(item.value) : updatedStagedSelected.add(item.value),
      ),
    );

    this.update({stagedSelected: updatedStagedSelected});
    this.updateSections(sectionItems);
  }

  onCommitClick() {
    this.dispatchSelectedEvent();

    const selectionDetail = this.getSelectionDetail();
    this.trackSelection({
      eventName: `Hide events`,
      props: {
        action: selectionDetail.action,
        '# of hidden events': selectionDetail.selectedCount,
        '# of visible events': selectionDetail.unselectedCount,
      },
    });
  }

  handleItemClick(item) {
    const {sectionItems} = this.state;

    this.updateSelectedValues(item);
    this.updateSections(sectionItems);
  }

  handleSelectionChange() {
    this.resetStagedSelection();
    this.updateSections(this.state.eventData, true);
    this.updateSelectedLabel();
  }

  // when 'inverted' is set, the list takes
  // all events as being selected excluding the
  // ones not in `selected`
  resetStagedSelection() {
    let {selected, eventData} = this.state;

    if (this.attr(`inverted`)) {
      selected = eventData.filter((event) => !selected.has(event.value)).map((event) => event.value);
    }

    this.update({stagedSelected: new Set(selected)});
  }

  getSections() {
    if (this.state.isMenuLoading) {
      return [
        {
          isLoading: true,
          items: [],
        },
      ];
    }

    return [{items: this.state.sectionItems}];
  }

  // re-organizes current menu items by sorting
  // them by selection state
  updateSections(source, reorderItems = false) {
    const {stagedSelected} = this.state;

    let sectionItems = source.map((item) => ({
      ...item,
      isSelected: stagedSelected.has(item.value),
    }));

    if (reorderItems) {
      const [selected, unselected] = partition(sectionItems, (item) => item.isSelected);

      if (selected.length <= unselected.length) {
        sectionItems = selected.concat(unselected);
      } else {
        sectionItems = unselected.concat(selected);
      }
    }

    this.update({sectionItems});
  }

  // store selectedLabel in state as it must
  // be deferred to commited selection changes
  selectedLabel() {
    return this.state.selectedLabel;
  }

  // the selectedLabel value must be updated only
  // after selection changes are commited
  updateSelectedLabel() {
    const defaultLabel = this.config.defaultState.selectedLabel;
    const {action, selectedCount, unselectedCount} = this.getSelectionDetail();
    let suffix = ``;

    if (action === `show`) {
      suffix = ` · all but ${unselectedCount}`;
    } else if (action === `hide` && selectedCount > 0) {
      suffix = ` · ${selectedCount}`;
    }

    this.update({selectedLabel: `${defaultLabel}${suffix}`});
  }

  updateSelectedValues(item) {
    const stagedSelected = new Set(this.state.stagedSelected);
    const value = item.value;

    if (stagedSelected.has(value)) {
      stagedSelected.delete(value);
    } else {
      stagedSelected.add(value);
    }

    this.update({stagedSelected});
  }

  dispatchSelectedEvent() {
    const {eventData, stagedSelected} = this.state;
    const selectionDetail = this.getSelectionDetail();

    this.dispatchEvent(
      new CustomEvent(`select`, {
        detail: {
          ...selectionDetail,
          selected: Array.from(stagedSelected),
          unselected: eventData.filter((item) => !stagedSelected.has(item.value)).map((item) => item.value),
        },
      }),
    );
  }

  getCommitLabel() {
    const {selectedCount} = this.getSelectionDetail();

    if (selectedCount === 0) {
      return `Apply`;
    }

    return `Hide ${selectedCount} ${pluralize(`event`, selectedCount)}`;
  }

  getSelectionDetail(source) {
    const {eventData, stagedSelected} = this.state;
    const selectedCount = (source || stagedSelected).size;
    const unselectedCount = eventData.length - selectedCount;
    let action = `hide`;

    // when selected items take most of the list
    // users are just showing a subset of events
    if (unselectedCount && unselectedCount < selectedCount) {
      action = `show`;
    }

    return {
      action,
      selectedCount,
      unselectedCount,
    };
  }
}

export const MultiEventsMenuEl = defineAndMakeJsxFactory(`mp-multi-events-menu`, MultiEventsMenu);
